import { formatMinute } from "@convex/lib/time";
import type { LessonStatus } from "@/lib/status";

/** One lesson as the calendar grid needs it. Mapped in `routes/Calendar.tsx`. */
export interface CalendarLesson {
  id: string;
  title: string;
  shortCode?: string;
  /** Minutes from local midnight, wall-clock in the app's timezone. */
  startMinute: number;
  endMinute: number;
  room?: string;
  category?: string;
  status?: LessonStatus;
  color?: string;
  cancelled?: boolean;
  partialAudio?: boolean;
  /** A weak automatic lesson match, PLAN.md §9 - worth a human glance. */
  needsReview?: boolean;
  notes?: string;
}

export type CalendarView = "week" | "month";

export const WEEKDAYS = [
  { short: "Mon", full: "MONDAY", isWeekend: false },
  { short: "Tue", full: "TUESDAY", isWeekend: false },
  { short: "Wed", full: "WEDNESDAY", isWeekend: false },
  { short: "Thu", full: "THURSDAY", isWeekend: false },
  { short: "Fri", full: "FRIDAY", isWeekend: false },
  { short: "Sat", full: "SATURDAY", isWeekend: true },
  { short: "Sun", full: "SUNDAY", isWeekend: true },
] as const;

export const pad = (n: number) => String(n).padStart(2, "0");

/** Local "YYYY-MM-DD" for a Date, matching how lessons are keyed. */
export function dateKeyOf(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The Monday of the week `date` falls in, at local midnight. */
export function mondayOf(date: Date): Date {
  const monday = new Date(date);
  monday.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  return monday;
}

export function lessonTimeLabel(lesson: CalendarLesson): string {
  return `${formatMinute(lesson.startMinute)} - ${formatMinute(lesson.endMinute)}`;
}

export type CalendarDay = {
  date: Date;
  dateKey: string;
  dayNumber: number;
  formattedDate: string;
  weekdayShort: string;
  weekdayFull: string;
  isWeekend: boolean;
  isToday: boolean;
  inCursorMonth: boolean;
};

function buildDay(date: Date, cursor: Date, todayKey: string): CalendarDay {
  const index = (date.getDay() + 6) % 7;
  return {
    date,
    dateKey: dateKeyOf(date),
    dayNumber: date.getDate(),
    formattedDate: `${pad(date.getDate())}.${pad(date.getMonth() + 1)}`,
    weekdayShort: WEEKDAYS[index].short,
    weekdayFull: WEEKDAYS[index].full,
    isWeekend: WEEKDAYS[index].isWeekend,
    isToday: dateKeyOf(date) === todayKey,
    inCursorMonth: date.getMonth() === cursor.getMonth(),
  };
}

/**
 * The days the given view puts on screen: seven for a week, or whole Monday-to-
 * Sunday weeks padded out to cover the cursor's month.
 */
export function buildDays(cursor: Date, view: CalendarView): CalendarDay[] {
  const start =
    view === "week"
      ? mondayOf(cursor)
      : mondayOf(new Date(cursor.getFullYear(), cursor.getMonth(), 1));

  let count = 7;
  if (view === "month") {
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const lastSunday = mondayOf(monthEnd);
    lastSunday.setDate(lastSunday.getDate() + 6);
    // Whole days apart, so DST cannot round this to the wrong week count.
    count = Math.round((lastSunday.getTime() - start.getTime()) / 86_400_000) + 1;
  }

  const todayKey = dateKeyOf(new Date());
  const days: CalendarDay[] = [];
  for (let i = 0; i < count; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    days.push(buildDay(date, cursor, todayKey));
  }
  return days;
}

/**
 * The first and last local date the given view shows. `routes/Calendar.tsx`
 * asks the server for exactly this span, so every cell it renders has data.
 */
export function visibleRange(
  cursor: Date,
  view: CalendarView,
): { from: string; to: string } {
  const days = buildDays(cursor, view);
  return { from: days[0].dateKey, to: days[days.length - 1].dateKey };
}
